import {GanttChartRows} from '@hconnect/ganttchart/src/modules'
import {Canvas2D} from '@hconnect/ganttchart/src/modules/canvas/Canvas2D'
import {GanttChartXAxis} from '@hconnect/ganttchart/src/modules/GanttChartXAxis'
import {GanttChartBaseRow} from '@hconnect/ganttchart/src/types/common'
import {AssetConfigurationDto} from '@kilnfeature/types'
import {useEffect, useRef, useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {drawYAxisWithLabels, drawDrillingZoneLines} from './helpers'

import {ChartLegend} from '@refra/modules/common/components'
import {mapColorConfig} from '@refra/modules/refra-planning/helpers/mapColorConfig'
import {DrillingZone} from '@refra/modules/refra-planning/types'

const DEFAULT_STEP_WIDTH = 15
const DEFAULT_Y_AXIS_LABEL_WIDTH = 50

export const DrillingZonesChart = ({
  kilnConfig,
  canvasRowHeight
}: {
  kilnConfig: AssetConfigurationDto
  canvasRowHeight: number
}) => {
  const {watch} = useFormContext<{drillingZones: DrillingZone[]}>()
  const kilnLength = Number(kilnConfig.payload.configuration.dimensions.length)
  const xAxis = Array.from({length: kilnLength}, (_, index) => index)
  const ganttContainerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const yLabelCanvasRef = useRef<HTMLCanvasElement>(null)
  const [stepWidth, setStepWidth] = useState(DEFAULT_STEP_WIDTH)

  const formWatch = watch()
  const colorConfig = mapColorConfig(kilnConfig, stepWidth, [1])

  useEffect(() => {
    const handleResize = () => {
      if (ganttContainerRef.current) {
        setStepWidth(ganttContainerRef.current.clientWidth / xAxis.length)
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [xAxis.length])

  const ganttChartRows = [
    {
      id: 1,
      name: ''
    }
  ]

  const canvasWidth = stepWidth * xAxis.length

  // Draw the lines between drilling zones
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d')

    const zones = formWatch.drillingZones

    if (!ctx || !zones || zones.length === 0) return

    // Configure canvas styles
    ctx.setLineDash([4, 4])
    ctx.strokeStyle = 'red'
    ctx.lineWidth = 1.2

    drawDrillingZoneLines({
      ctx,
      zones,
      stepWidth,
      canvasHeight: canvasRowHeight
    })
  }, [canvasRowHeight, formWatch, stepWidth])

  // Update the y-axis labels
  useEffect(() => {
    const ctx = yLabelCanvasRef.current?.getContext('2d')
    if (!ctx || !yLabelCanvasRef.current) return

    ctx.clearRect(0, 0, yLabelCanvasRef.current.width, yLabelCanvasRef.current.height)
    drawYAxisWithLabels({
      ctx,
      canvasHeight: canvasRowHeight,
      yMax: canvasRowHeight,
      interval: Number((canvasRowHeight / 15).toFixed())
    })
  }, [canvasRowHeight, formWatch])

  return (
    <div>
      <div className="flex justify-start ml-4 mb-3" data-cy="drillings_chart_legend">
        <ChartLegend
          displayItems={['drive', 'tires', 'threshold']}
          translationKey={'plantSetup.drillingZone'}
        />
      </div>
      <div className="flex w-full h-full" data-test-id="drilling_zones_gantt_chart">
        <div className={`w-[${DEFAULT_Y_AXIS_LABEL_WIDTH}px] h-full`}>
          <Canvas2D
            width={DEFAULT_Y_AXIS_LABEL_WIDTH}
            height={canvasRowHeight}
            ref={yLabelCanvasRef}
          />
        </div>
        <div className="relative flex-1  h-full">
          <div ref={ganttContainerRef} className=" h-full w-full">
            <GanttChartRows<GanttChartBaseRow, number>
              chartStartBoundary={0}
              chartEndBoundary={kilnLength}
              stepWidth={stepWidth}
              colorConfig={colorConfig}
              isReadOnly={false}
              xAxis={xAxis}
              isAddItemEnabled={false}
              ganttChartRows={ganttChartRows}
              canvasRef={canvasRef}
              rowHeight={canvasRowHeight}
            />
          </div>
        </div>
      </div>
      <GanttChartXAxis
        canvasWidth={canvasWidth}
        kilnLength={kilnLength}
        stepWidth={stepWidth}
        yAxisLabelWidth={DEFAULT_Y_AXIS_LABEL_WIDTH}
      />
    </div>
  )
}
